import { element } from "./dom.ts";
import type { CitationGraph } from "../graph/builders.ts";
import { isSemanticDestination, isVisualOnlyDestinationId } from "../graph/semantic-identity.ts";

export type MapNavigationAction =
  | { readonly kind: "focus"; readonly index: number }
  | { readonly kind: "activate"; readonly index: number }
  | { readonly kind: "close-navigation" }
  | { readonly kind: "none" };

export type MapDestination = {
  readonly id: string;
  readonly label: string;
  readonly count: number;
};

export type DestinationEventDetail = { readonly destination: MapDestination };
export type OutwardTarget = "overview" | "macro" | "meso";
export type OutwardNavigationDetail = { readonly target: OutwardTarget };

export function destinationEventDetail(destination: MapDestination): DestinationEventDetail | null {
  return isVisualOnlyDestinationId(destination.id) ? null : { destination };
}

export function destinationFromEvent(event: Event, graph: CitationGraph): MapDestination | null {
  if (!(event instanceof CustomEvent) || !isRecord(event.detail)) return null;
  const destination = event.detail["destination"];
  if (!isRecord(destination)) return null;
  const id = destination["id"];
  const label = destination["label"];
  const count = destination["count"];
  return typeof id === "string" && isSemanticDestination(graph, id) && typeof label === "string" && Number.isSafeInteger(count) && typeof count === "number" && count >= 0
    ? { id, label, count }
    : null;
}

export function outwardNavigationDetail(target: OutwardTarget): OutwardNavigationDetail {
  return { target };
}

export function mapNavigationAction(key: string, index: number, count: number, enabled = true): MapNavigationAction {
  if (!enabled) return { kind: "none" };
  if (key === "Escape") return { kind: "close-navigation" };
  if (count === 0) return { kind: "none" };
  if (key === "Enter" || key === " ") return { kind: "activate", index };
  if (key === "ArrowDown" || key === "ArrowRight") return { kind: "focus", index: (index + 1) % count };
  if (key === "ArrowUp" || key === "ArrowLeft") return { kind: "focus", index: (index - 1 + count) % count };
  return { kind: "none" };
}

export function renderMapNavigation(nav: HTMLElement, destinations: readonly MapDestination[]): void {
  const existing = new Map([...nav.querySelectorAll<HTMLButtonElement>("button[data-destination-id]")].map((button) => [button.dataset["destinationId"] ?? "", button]));
  if (destinations.length === 0) {
    nav.replaceChildren();
    nav.append(element("p", { class: "map-navigation-empty" }, ["Kaartgebieden verschijnen zodra de atlas is geladen."]));
    return;
  }
  const previousRovingId = [...existing.values()].find((button) => button.tabIndex === 0)?.dataset["destinationId"] ?? null;
  const rovingId = rovingDestinationId(destinations, previousRovingId);
  const buttons = destinations.map((destination) => {
    const button = existing.get(destination.id) ?? element("button", { type: "button" }, [element("span"), element("small")]);
    const tabIndex = destination.id === rovingId ? 0 : -1;
    const countValue = String(destination.count);
    const ariaLabel = `${destination.label}, ${destination.count.toLocaleString("nl-NL")} citatieverbindingen`;
    if (button.tabIndex !== tabIndex) button.tabIndex = tabIndex;
    if (button.dataset["destinationId"] !== destination.id) button.dataset["destinationId"] = destination.id;
    if (button.dataset["destinationLabel"] !== destination.label) button.dataset["destinationLabel"] = destination.label;
    if (button.dataset["destinationCount"] !== countValue) button.dataset["destinationCount"] = countValue;
    if (button.getAttribute("aria-label") !== ariaLabel) button.setAttribute("aria-label", ariaLabel);
    const label = button.querySelector("span");
    const count = button.querySelector("small");
    if (label !== null && label.textContent !== destination.label) label.textContent = destination.label;
    const countText = destination.count.toLocaleString("nl-NL");
    if (count !== null && count.textContent !== countText) count.textContent = countText;
    return button;
  });
  const sameOrder = buttons.length === nav.children.length && buttons.every((button, index) => nav.children.item(index) === button);
  if (!sameOrder) nav.replaceChildren(...buttons);
}

export function rovingDestinationId(destinations: readonly MapDestination[], previousId: string | null): string | null {
  return destinations.some((destination) => destination.id === previousId) ? previousId : destinations[0]?.id ?? null;
}

export function bindMapNavigation(nav: HTMLElement): void {
  nav.addEventListener("click", (event) => {
    if (nav.getAttribute("aria-disabled") === "true") return;
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("button[data-destination-id]") : null;
    if (button === null || !nav.contains(button)) return;
    const id = button.dataset["destinationId"];
    const label = button.dataset["destinationLabel"];
    const count = Number(button.dataset["destinationCount"]);
    if (id === undefined || label === undefined || !Number.isSafeInteger(count)) return;
    const detail = destinationEventDetail({ id, label, count });
    if (detail !== null) nav.dispatchEvent(new CustomEvent<DestinationEventDetail>("atlas:destination", { bubbles: true, detail }));
  });
  nav.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    const buttons = [...nav.querySelectorAll<HTMLButtonElement>("button[data-destination-id]")];
    const target = event.target instanceof HTMLButtonElement ? event.target : null;
    const index = target === null ? 0 : Math.max(0, buttons.indexOf(target));
    const action = mapNavigationAction(event.key, index, buttons.length, nav.getAttribute("aria-disabled") !== "true");
    if (action.kind === "none") return;
    event.preventDefault();
    if (action.kind === "focus") {
      buttons.forEach((button, itemIndex) => button.tabIndex = itemIndex === action.index ? 0 : -1);
      buttons[action.index]?.focus();
      return;
    }
    if (action.kind === "activate") {
      buttons[action.index]?.click();
      return;
    }
    nav.dispatchEvent(new CustomEvent("atlas:close-navigation", { bubbles: true }));
  });
}


function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
